/**
 * =============================================================================
 * WIDGET API CLIENT
 * =============================================================================
 *
 * API client for the storefront widget.
 * Handles communication with the Worker API for question submission,
 * image uploads, and retrieving user's questions.
 *
 * ARCHITECTURE REFERENCE: PHASE_0B_SPEC.md §2.5.1
 * PATTERN REFERENCE: PHASE_0B_PATTERNS.md §8
 *
 * =============================================================================
 */

import type {
  ApiResponse,
  SubmitQuestionRequest,
  SubmitReplyResponse,
  PublicQuestionListItem,
  QuestionListResponse,
  PublicQuestionDetail,
  QuestionDetailResponse,
  PublicReply,
  PaginationMeta,
  PublicVisibleStatus,
  TagSuggestionResponse,
  PublicTagTaxonomyResponse,
  PublicReputationResponse,
  LeaderboardResponse,
  PublicAvailability,
  UploadCapabilityDescriptor,
  ManageCapabilityDescriptor,
} from '@find-my-part/types';
import { GUEST_CAPABILITY } from '@find-my-part/types';

import {
  DEFAULT_REQUEST_TIMEOUT_MS,
  fetchWithRetry,
  OfflineError,
  MaxRetriesExceededError,
  RequestTimeoutError,
  type RetryOptions,
} from './fetchWithRetry';
import { logger } from '../lib/logger';

// =============================================================================
// STORE HASH EXTRACTION
// =============================================================================

/**
 * Check if running in development mode.
 * Used to enable dev-specific fallbacks like VITE_STORE_HASH.
 */
function isDevelopmentMode(): boolean {
  return (
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  );
}

/**
 * Get store hash from widget script URL or environment variable.
 *
 * Resolution order:
 * 1. Script URL query parameter: widget.js?store={storeHash} (production)
 * 2. Vite environment variable: VITE_STORE_HASH (development only)
 *
 * ⚠️ GOTCHA: document.currentScript is null for ES module scripts and after
 *    async operations. In development mode, use VITE_STORE_HASH instead.
 *
 * @see docs/onboarding/06b-manual-testing.md for dev setup instructions
 */
function getStoreHash(): string | null {
  // Capture currentScript at load time (before any async operations)
  const script = document.currentScript as HTMLScriptElement | null;

  // Try to get store hash from script URL (production mode)
  if (script?.src) {
    try {
      const url = new URL(script.src);
      const storeHash = url.searchParams.get('store');

      if (storeHash) {
        return storeHash;
      }
    } catch (error) {
      logger.error('SCRIPT_URL_PARSE_FAILURE', error instanceof Error ? error : null);
    }
  }

  // Development fallback: check for VITE_STORE_HASH environment variable
  // This allows local testing without the production script URL mechanism
  if (isDevelopmentMode()) {
    const envStoreHash =
      typeof import.meta !== 'undefined' ? import.meta.env?.VITE_STORE_HASH : undefined;

    if (envStoreHash) {
      // Development-only diagnostic: helps developers confirm which store hash source is being used
      // during local testing. This log only appears on localhost with VITE_STORE_HASH configured.
      logger.info('DEV_STORE_HASH');
      return envStoreHash;
    }

    // Provide helpful error message for developers
    logger.error('DEV_STORE_HASH_MISSING', null, {
      hint: 'Create packages/storefront-widget/.env with VITE_STORE_HASH=your_store_hash and VITE_API_BASE_URL=http://localhost:8787',
    });
    return null;
  }

  // Production mode without script URL - this shouldn't happen
  logger.error('STORE_CONTEXT_UNAVAILABLE', null, { reason: 'no script src' });
  return null;
}

// Capture at module load time
const STORE_HASH = getStoreHash();

/**
 * Session-scoped correlation ID for the widget (P2-27).
 * Generated once at widget load, used for all subsequent requests.
 * Enables tracing an entire user session through backend logs.
 */
const SESSION_CORRELATION_ID = crypto.randomUUID();

/**
 * Get the API base URL from environment or default.
 * In development, this can be overridden via Vite's env variables.
 */
function getApiBaseUrl(): string {
  // Check for Vite environment variable (per PHASE_0B_SPEC.md §2.5.1)
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  // Default to empty string (same origin) for production
  return '';
}

const API_BASE = getApiBaseUrl();

/**
 * Severity + message returned by {@link diagnoseApiBaseUrl}. `severity:
 * 'ok'` means the configuration is internally consistent (or we can't
 * judge it — e.g. server-side rendering, no `window`); `severity:
 * 'warn'` means the bundle was almost certainly built with the wrong
 * `VITE_API_BASE_URL` and `/api/public/*` calls will be blocked at
 * runtime.
 */
export interface ApiBaseUrlDiagnosis {
  severity: 'ok' | 'warn';
  /** Stable, greppable error tag printed to the Console on `'warn'`. */
  code?: 'WIDGET_BUILD_MISCONFIGURED_API_BASE';
  /** Human-readable explanation, suitable for a Console error line. */
  message?: string;
  /** Echoes the resolved API_BASE for diagnostics. Never includes a token. */
  apiBase: string;
  /** Echoes the runtime hostname. */
  hostname?: string;
}

/**
 * Pure helper. Detects the failure mode discovered on the OGS sandbox
 * 2026-05-08T23:09Z visible run: the storefront-widget bundle was
 * built with `VITE_API_BASE_URL=http://localhost:8787` baked in, so
 * every `/api/public/*` call from a real BC storefront page targeted
 * loopback. Chrome's Private Network Access (PNA) policy blocked the
 * calls under "local network requests"; mixed-content (HTTP from
 * HTTPS) would have blocked them anyway. The widget rendered defaults
 * for views that don't need a config response (Ask form), and other
 * views silently had no data.
 *
 * Returns `'warn'` only when:
 *   1. We have a real `window.location.hostname` (i.e. running in a
 *      browser, not jsdom/SSR).
 *   2. That hostname is NOT a loopback / `*.local` / `*.localhost`.
 *      (The dev harness runs on `localhost`, where loopback API_BASE
 *      is correct.)
 *   3. AND the resolved `apiBase` IS a loopback / non-HTTPS URL.
 *
 * All three must be true simultaneously, so this never warns in dev,
 * never warns in tests, never warns server-side.
 *
 * NOTE: passing `apiBase=''` (the production same-origin default)
 * always returns `'ok'`. The misconfiguration we're catching is
 * "explicit baked-in localhost", not "empty default".
 */
export function diagnoseApiBaseUrl(input: {
  apiBase: string;
  hostname: string | undefined;
}): ApiBaseUrlDiagnosis {
  const { apiBase, hostname } = input;
  if (!hostname) {
    return { severity: 'ok', apiBase, hostname };
  }

  if (isLoopbackOrPrivateHost(hostname)) {
    // Running on localhost / loopback / RFC 1918 / .local — loopback or
    // private API_BASE is plausible there too. Don't fire the
    // diagnostic; both sides are inside the same not-actually-public
    // network position.
    return { severity: 'ok', apiBase, hostname };
  }

  if (!apiBase) {
    // Empty same-origin default — fine on any host.
    return { severity: 'ok', apiBase, hostname };
  }

  // Look at apiBase's host. If unparseable, treat as ok (we don't want
  // to block on a bug in the diagnostic itself).
  let apiBaseHost = '';
  let apiBaseProtocol = '';
  try {
    const u = new URL(apiBase);
    apiBaseHost = u.hostname.toLowerCase();
    apiBaseProtocol = u.protocol;
  } catch {
    return { severity: 'ok', apiBase, hostname };
  }

  if (isLoopbackOrPrivateHost(apiBaseHost) || apiBaseProtocol === 'http:') {
    return {
      severity: 'warn',
      code: 'WIDGET_BUILD_MISCONFIGURED_API_BASE',
      apiBase,
      hostname,
      message: [
        `WIDGET_BUILD_MISCONFIGURED_API_BASE: the widget is running on a public`,
        `storefront (hostname=${hostname}) but its baked-in API base is`,
        `"${apiBase}". Browsers will block /api/public/* requests under Chrome`,
        `Private Network Access policy ("local network requests"; covers`,
        `loopback 127/8, RFC 1918 private IPv4 ranges 10/8, 172.16/12, 192.168/16,`,
        `and *.local mDNS hosts) and/or mixed-content rules, so the widget will`,
        `appear to render but Browse / My Questions / question detail views`,
        `will have no data.`,
        ``,
        `Fix: rebuild the widget bundle with`,
        `    VITE_API_BASE_URL=https://<your-public-https-worker>`,
        `and redeploy / re-tunnel / re-CDN it. Verify in DevTools Network`,
        `panel that /api/public/* requests target the public host, and Chrome`,
        `Issues has no "local network requests" entries. The Network-panel`,
        `evidence is the source of truth for sign-off; absence of this Console`,
        `error is necessary but not sufficient.`,
        ``,
        `Tracked as BL-MSPL-WIDGET-API-ORIGIN-001 in adr-004-mode2-validation-results.md.`,
      ].join('\n'),
    };
  }

  return { severity: 'ok', apiBase, hostname };
}

/**
 * Returns true when the host is in any of the address ranges Chrome's
 * Private Network Access policy treats as "local". Covers:
 *   - The literal `localhost` and `0.0.0.0` strings.
 *   - The full IPv4 loopback range `127.0.0.0/8`.
 *   - RFC 1918 private IPv4 ranges:
 *       `10.0.0.0/8`
 *       `172.16.0.0/12`
 *       `192.168.0.0/16`
 *   - IPv6 loopback `::1` (with or without brackets).
 *   - mDNS / merchant-LAN hostnames `*.local` and `*.localhost`.
 *
 * Does NOT cover IPv4-mapped IPv6 (e.g. `::ffff:10.0.0.1`) or IPv6
 * unique-local addresses (`fc00::/7`) — both are very unlikely in this
 * codebase's actual config files and adding them would expand the
 * surface without observed need.
 */
function isLoopbackOrPrivateHost(host: string): boolean {
  const lower = host.toLowerCase();
  if (
    lower === 'localhost' ||
    lower === '0.0.0.0' ||
    lower === '[::1]' ||
    lower === '::1'
  ) {
    return true;
  }
  if (lower.endsWith('.local') || lower.endsWith('.localhost')) {
    return true;
  }
  // IPv4 dotted-quad ranges.
  const m = lower.match(/^(\d+)\.(\d+)\.(\d+)\.(\d+)$/);
  if (!m) return false;
  const a = Number(m[1]);
  const b = Number(m[2]);
  const c = Number(m[3]);
  const d = Number(m[4]);
  // Reject malformed octets.
  for (const octet of [a, b, c, d]) {
    if (!Number.isFinite(octet) || octet < 0 || octet > 255) return false;
  }
  // 127.0.0.0/8 — entire IPv4 loopback range (not just 127.0.0.1).
  if (a === 127) return true;
  // RFC 1918 private ranges.
  if (a === 10) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  if (a === 192 && b === 168) return true;
  return false;
}

// Run the diagnostic ONCE at module load and emit a tagged Console
// error if misconfigured. This is intentional: the cost of a one-off
// pure-function call at bundle init is negligible, and the diagnostic
// is only useful if it fires before the first failed API request (so
// the operator sees it without having to trigger the failure first).
// The check is sandboxed — a thrown helper would still let the rest of
// the bundle initialise.
try {
  if (typeof window !== 'undefined') {
    const diagnosis = diagnoseApiBaseUrl({
      apiBase: API_BASE,
      hostname: window.location?.hostname,
    });
    if (diagnosis.severity === 'warn') {
      // Stable code: WIDGET_BUILD_MISCONFIGURED_API_BASE. The fallback
      // string here matches the canonical value to keep grep results
      // consistent in case `diagnosis.code` is ever returned undefined
      // by a future refactor.
      logger.error(diagnosis.code ?? 'WIDGET_BUILD_MISCONFIGURED_API_BASE', null, {
        apiBase: diagnosis.apiBase,
        hostname: diagnosis.hostname,
        message: diagnosis.message,
      });
    }
  }
} catch {
  // Diagnostic is best-effort; never break bundle init.
}

// =============================================================================
// API REQUEST HELPER
// =============================================================================

/**
 * Options for API requests
 */
interface ApiRequestOptions extends RequestInit {
  /** AbortController signal for request cancellation */
  signal?: AbortSignal;
}

function protocolErrorResponse<T>(): ApiResponse<T> {
  return {
    success: false,
    error: {
      code: 'PROTOCOL_ERROR',
      message: 'The server returned an invalid response. Please try again.',
    },
  };
}

function networkErrorResponse<T>(): ApiResponse<T> {
  return {
    success: false,
    error: {
      code: 'NETWORK_ERROR',
      message: 'Network request failed',
    },
  };
}

function isStructuredApiFailure(value: unknown): boolean {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const envelope = value as Record<string, unknown>;
  if (!Object.prototype.hasOwnProperty.call(envelope, 'success') || envelope.success !== false) {
    return false;
  }
  if (!Object.prototype.hasOwnProperty.call(envelope, 'error')) return false;
  const error = envelope.error;
  if (typeof error !== 'object' || error === null || Array.isArray(error)) return false;
  const errorRecord = error as Record<string, unknown>;
  return (
    Object.prototype.hasOwnProperty.call(errorRecord, 'code') &&
    typeof errorRecord.code === 'string' &&
    Object.prototype.hasOwnProperty.call(errorRecord, 'message') &&
    typeof errorRecord.message === 'string'
  );
}

function isResponseBodyTransportFailure(error: unknown): boolean {
  if ((typeof error !== 'object' && typeof error !== 'function') || error === null) {
    return false;
  }

  // Rejections can originate in the host page, an iframe, or the fetch
  // implementation's realm. `instanceof` is therefore not a stable browser
  // boundary. Error names are the interoperable signal here; malformed JSON
  // remains a SyntaxError and is intentionally excluded.
  try {
    const name = (error as { name?: unknown }).name;
    return name === 'TypeError' || name === 'AbortError' || name === 'NetworkError';
  } catch {
    return false;
  }
}

async function readJsonFromOwnedReader(
  reader: ReadableStreamDefaultReader<Uint8Array>
): Promise<unknown> {
  const decoder = new TextDecoder();
  const parts: string[] = [];

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      parts.push(decoder.decode(value, { stream: true }));
    }
    parts.push(decoder.decode());
    return JSON.parse(parts.join('')) as unknown;
  } finally {
    reader.releaseLock();
  }
}

/** Bound response-body consumption after fetch has delivered the headers. */
function readJsonWithTimeout(response: Response, timeoutMs: number): Promise<unknown> {
  const reader = response.body?.getReader();
  const readPromise = reader ? readJsonFromOwnedReader(reader) : response.json();

  return new Promise((resolveJson, rejectJson) => {
    let settled = false;
    const timeoutId = setTimeout(() => {
      if (settled) return;
      settled = true;
      rejectJson(new RequestTimeoutError());
      // This helper owns the reader lock, so cancellation reaches the underlying
      // stream instead of failing against response.json()'s inaccessible reader.
      void reader?.cancel().catch(() => undefined);
    }, timeoutMs);

    void readPromise.then(
      (value: unknown) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        resolveJson(value);
      },
      (error: unknown) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        rejectJson(error);
      }
    );
  });
}

function widgetInitErrorResponse<T>(): ApiResponse<T> {
  return {
    success: false,
    error: {
      code: 'WIDGET_INIT_ERROR',
      message: 'Widget initialization failed: store context missing',
    },
  };
}

/**
 * Shared transport core. Callers supply the COMPLETE `RequestInit` — this
 * function never adds a credential, header, or method of its own, so each
 * caller's credential posture is visible at its own call site rather than
 * being decided here.
 *
 * Both {@link apiRequest} (credentialed) and {@link anonymousGetRequest}
 * (ADR-028 Slice 3 cacheable public reads) delegate here so the retry,
 * timeout, structured-envelope, body-read and error-classification behaviour
 * stays identical across both postures.
 */
async function executeRequest<T>(
  endpoint: string,
  requestInit: RequestInit,
  retryOptions?: RetryOptions
): Promise<ApiResponse<T>> {
  try {
    // Use fetchWithRetry for automatic retry on network/5xx errors
    const response = await fetchWithRetry(`${API_BASE}${endpoint}`, requestInit, retryOptions);
    const bodyTimeoutMs = retryOptions?.timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;

    // A completed HTTP exchange is not a network failure. Preserve valid
    // structured error envelopes, but classify invalid JSON as a protocol
    // failure so the UI never tells an online customer to troubleshoot their
    // internet connection for a malformed server or intermediary response.
    if (!response.ok) {
      try {
        const errorData = await readJsonWithTimeout(response, bodyTimeoutMs);
        return isStructuredApiFailure(errorData)
          ? (errorData as ApiResponse<T>)
          : protocolErrorResponse<T>();
      } catch (error) {
        if (error instanceof RequestTimeoutError) throw error;
        if (isResponseBodyTransportFailure(error)) return networkErrorResponse<T>();
        return protocolErrorResponse<T>();
      }
    }

    try {
      return (await readJsonWithTimeout(response, bodyTimeoutMs)) as ApiResponse<T>;
    } catch (error) {
      if (error instanceof RequestTimeoutError) throw error;
      if (isResponseBodyTransportFailure(error)) return networkErrorResponse<T>();
      return protocolErrorResponse<T>();
    }
  } catch (error) {
    // P1-11: Handle request timeout
    if (error instanceof RequestTimeoutError) {
      return {
        success: false,
        error: {
          code: 'REQUEST_TIMEOUT',
          message: error.message,
        },
      };
    }

    // Handle offline error
    if (error instanceof OfflineError) {
      return {
        success: false,
        error: {
          code: 'OFFLINE',
          message: error.message,
        },
      };
    }

    // Handle max retries exceeded
    if (error instanceof MaxRetriesExceededError) {
      return {
        success: false,
        error: {
          code: 'MAX_RETRIES_EXCEEDED',
          message: error.message,
        },
      };
    }

    // Other network errors (CORS, etc.)
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: error instanceof Error ? error.message : 'Network request failed',
      },
    };
  }
}

/**
 * Make a credentialed API request with store context.
 *
 * Sends `credentials: 'include'` so the `guest_token` cookie rides along. Every
 * mutation and every visitor-specific read (notably `/my-questions`) uses this
 * path, and responses from it are NEVER eligible for the ADR-028 Slice 3
 * public-read cache.
 *
 * @param endpoint - API endpoint path (e.g., '/api/public/questions')
 * @param options - Fetch options including optional AbortSignal
 * @returns Parsed API response
 */
async function apiRequest<T>(
  endpoint: string,
  options: ApiRequestOptions = {},
  retryOptions?: RetryOptions
): Promise<ApiResponse<T>> {
  if (!STORE_HASH) return widgetInitErrorResponse<T>();

  return executeRequest<T>(
    endpoint,
    {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'X-Store-Hash': STORE_HASH,
        'X-Correlation-ID': SESSION_CORRELATION_ID, // P2-27: Session tracing
        ...options.headers,
      },
      credentials: 'include', // Include cookies (guest_token)
    },
    retryOptions
  );
}

/**
 * The ONLY option a caller may supply to {@link anonymousGetRequest}.
 *
 * Deliberately NOT `RequestInit`: there is no `headers`, `method`, `body`, or
 * `credentials` member, so a caller cannot widen the anonymous posture, attach
 * an `Authorization` / cookie / upload-proof / manage-proof header, or turn a
 * cacheable read into a mutation. Closing the option type is what makes
 * "credentialed data can never reach cache admission" a structural property
 * rather than a convention (ADR-028 Slice 3; D-8).
 */
export interface AnonymousGetOptions {
  /** AbortSignal for cancellation. The only caller-controlled input. */
  signal?: AbortSignal;
}

/**
 * Anonymous, GET-only transport for cacheable public reads (ADR-028 Slice 3).
 *
 * Sends `credentials: 'omit'` — no cookies are attached and none are accepted —
 * and exactly two non-credential context headers: the tenant scope the Worker
 * requires to resolve the store, and the session correlation id used for log
 * tracing. `cache: 'no-store'` keeps the browser/intermediary HTTP cache from
 * becoming a second, URL-only cache authority that may ignore the tenant header;
 * the explicitly tenant-keyed in-memory resource remains the sole cache. `Content-Type`
 * is not sent because a GET carries no body.
 *
 * Because the response can never have been influenced by a cookie or a
 * capability, and because {@link AnonymousGetOptions} admits no header or
 * credential input, every response returned here is safe to cache. This is the
 * sole admission path into the public-read cache.
 */
async function anonymousGetRequest<T>(
  endpoint: string,
  options: AnonymousGetOptions = {},
  retryOptions?: RetryOptions
): Promise<ApiResponse<T>> {
  if (!STORE_HASH) return widgetInitErrorResponse<T>();

  return executeRequest<T>(
    endpoint,
    {
      method: 'GET',
      headers: {
        'X-Store-Hash': STORE_HASH,
        'X-Correlation-ID': SESSION_CORRELATION_ID,
      },
      credentials: 'omit',
      cache: 'no-store',
      signal: options.signal,
    },
    retryOptions
  );
}

/**
 * Make an API request for file upload (multipart/form-data).
 * Does not set Content-Type header - browser sets it with boundary.
 *
 * @param endpoint - API endpoint path
 * @param formData - FormData containing the file
 * @returns Parsed API response
 */
async function apiUpload<T>(
  endpoint: string,
  formData: FormData,
  uploadProof?: string
): Promise<ApiResponse<T>> {
  if (!STORE_HASH) {
    return {
      success: false,
      error: {
        code: 'WIDGET_INIT_ERROR',
        message: 'Widget initialization failed: store context missing',
      },
    };
  }

  try {
    const response = await fetchWithRetry(
      `${API_BASE}${endpoint}`,
      {
        method: 'POST',
        headers: {
          // Don't set Content-Type for FormData - browser sets it with boundary
          'X-Store-Hash': STORE_HASH,
          'X-Correlation-ID': SESSION_CORRELATION_ID, // P2-27: Session tracing
          // ADR-024 §11: cross-site upload-proof handle so ownership survives
          // third-party-cookie blocking. `credentials: 'include'` is retained (the
          // cookie remains the fallback on non-ITP browsers until the read-leg fix).
          ...(uploadProof ? { [GUEST_CAPABILITY.UPLOAD_PROOF_HEADER]: uploadProof } : {}),
        },
        credentials: 'include',
        body: formData,
      },
      // ADR-024 §11 (round-2 review): NO transport-level auto-retry on the
      // capability-bearing upload leg. Each retry re-sends X-Upload-Proof and the
      // server consumes one capability use per attempt (the middleware decrements
      // before the handler runs), so N transient-5xx retries would burn N uses
      // and could strand under-cap photos with an unrecoverable 400. App-level
      // retry is preserved via QuestionForm's "Retry failed photos" affordance,
      // which spends exactly one use per user-initiated attempt.
      { maxRetries: 0 }
    );

    // Handle non-JSON responses gracefully (4xx errors that weren't retried)
    if (!response.ok) {
      try {
        const errorData = await response.json();
        return errorData as ApiResponse<T>;
      } catch {
        return {
          success: false,
          error: {
            code: 'UPLOAD_FAILED',
            message: `Upload failed: ${response.status} ${response.statusText}`,
          },
        };
      }
    }

    return (await response.json()) as ApiResponse<T>;
  } catch (error) {
    // P1-11: Handle request timeout
    if (error instanceof RequestTimeoutError) {
      return {
        success: false,
        error: {
          code: 'REQUEST_TIMEOUT',
          message: error.message,
        },
      };
    }

    // Handle offline error
    if (error instanceof OfflineError) {
      return {
        success: false,
        error: {
          code: 'OFFLINE',
          message: error.message,
        },
      };
    }

    // Handle max retries exceeded
    if (error instanceof MaxRetriesExceededError) {
      return {
        success: false,
        error: {
          code: 'MAX_RETRIES_EXCEEDED',
          message: error.message,
        },
      };
    }

    // Other upload errors
    return {
      success: false,
      error: {
        code: 'UPLOAD_FAILED',
        message: error instanceof Error ? error.message : 'Upload failed',
      },
    };
  }
}

/**
 * Build the `file` multipart body and POST it to an image-upload endpoint.
 * Shared by the question and reply upload legs so the FormData contract lives
 * in one place; both re-send the ADR-024 §11 `uploadProof` as X-Upload-Proof.
 */
function uploadImageMultipart(
  endpoint: string,
  file: File,
  uploadProof?: string
): Promise<ApiResponse<UploadImageResult>> {
  const formData = new FormData();
  formData.append('file', file);
  return apiUpload<UploadImageResult>(endpoint, formData, uploadProof);
}

// =============================================================================
// RESPONSE TYPES
// =============================================================================

/**
 * Response for POST /api/public/questions
 */
export interface SubmitQuestionResult {
  questionId: string;
  slug: string;
  status: string;
  message: string;
  /**
   * Cross-site upload-proof capability (ADR-024 §11). When present, the widget
   * re-sends `token` as the `X-Upload-Proof` header on the immediate image
   * upload(s) so ownership survives third-party-cookie blocking. Held in memory
   * only — never persisted, never logged, never placed in a URL.
   */
  uploadCapability?: UploadCapabilityDescriptor;
  /**
   * Cross-site management capability (ADR-024 §10.4). When present, the widget
   * surfaces a "manage/delete your photos" success-screen link built from
   * `token` (the cookieless `/manage?token=…` magic-link). Held in memory only —
   * never persisted, never logged; the token appears only inside that link.
   */
  manageCapability?: ManageCapabilityDescriptor;
}

/**
 * Response for POST /api/public/questions/:questionId/images
 */
export interface UploadImageResult {
  attachmentId: string;
  url: string;
  filename: string;
  size: number;
  mimeType: string;
}

/**
 * Question summary for my-questions list
 */
export interface MyQuestionItem {
  questionId: string;
  title: string;
  status: string;
  /** Current Worker Date JSON plus the numeric legacy/rollout-skew representation. */
  createdAt: string | number;
  imageCount: number;
}

/**
 * Response for GET /api/public/my-questions
 *
 * ADR-024 item (c): the worker ALWAYS emits `historyAvailable` — `true` when a valid
 * guest identity resolved (even with zero questions), `false` when no valid identity was
 * present (missing / ITP-blocked / expired / cleared / malformed cookie). The field is
 * kept OPTIONAL on the client so a widget bundle deployed ahead of the worker (rollout
 * skew) still type-checks: `undefined` means "legacy server" and the widget preserves its
 * prior empty/list behaviour. Kept local to the widget this wave (no shared-types edit).
 */
export interface MyQuestionsResult {
  questions: MyQuestionItem[];
  historyAvailable?: boolean;
}

/**
 * Response for GET /api/public/config
 * Aligns with PublicConfig from @find-my-part/types
 */
export interface WidgetConfigResult {
  turnstileSiteKey: string;
  maxImagesPerSubmission: number;
  maxImageSizeMb: number;
}

/**
 * Similar question item from search results (P1-19)
 */
export interface SimilarQuestionItem {
  questionId: string;
  title: string;
  slug: string;
  status: string;
  replyCount: number;
  hasSolution: boolean;
  similarity: number;
  editoriallyCorrected: boolean;
}

/**
 * Response for GET /api/public/questions/similar
 */
export interface SimilarQuestionsResult {
  questions: SimilarQuestionItem[];
  total: number;
}

/** Public full-text result returned by the existing search endpoint (ADR-028 Slice 2C). */
export interface PublicQuestionSearchItem {
  questionId: string;
  title: string;
  slug: string;
  description: string | null;
  status: PublicVisibleStatus;
  replyCount: number;
  createdAt: number;
  highlightedTitle?: string;
  highlightedSnippet?: string;
  score: number;
  editoriallyCorrected: boolean;
}

/** Response for GET /api/public/questions/search. Kept widget-local; the API is unchanged. */
export interface PublicQuestionSearchResult {
  questions: PublicQuestionSearchItem[];
  pagination: PaginationMeta;
  searchTimeMs: number;
}

// Re-export shared types for widget consumers (GAP-005, GAP-007)
export type { PublicQuestionListItem, QuestionListResponse, PublicQuestionDetail, QuestionDetailResponse, PublicReply, PaginationMeta, TagSuggestionResponse, PublicTagTaxonomyResponse, PublicReputationResponse, LeaderboardResponse, PublicAvailability };

// =============================================================================
// WIDGET API CLIENT
// =============================================================================

/**
 * Get the session correlation ID for error reporting (P2-27).
 * Can be displayed to users for support reference.
 */
export function getSessionCorrelationId(): string {
  return SESSION_CORRELATION_ID;
}

/**
 * Build the absolute ADR-024 §10.4 management magic-link for a MANAGE handle.
 *
 * Reuses the same `API_BASE` as every other worker call, so the link targets the
 * worker origin the widget already talks to — absolute in production (where
 * `VITE_API_BASE_URL` is baked into the bundle) and localhost in dev. The opaque
 * handle rides the `?token=` query via `encodeURIComponent`. This is the ONE
 * place the token is allowed to appear in a URL (the intended magic-link); it is
 * never logged or persisted.
 */
export function buildManageUrl(token: string): string {
  return `${API_BASE}/api/public/manage?token=${encodeURIComponent(token)}`;
}

/** Options accepted by the public question list read. */
export interface PublicQuestionListOptions {
  page?: number;
  limit?: number;
  status?: readonly PublicVisibleStatus[];
}

/**
 * Build the canonical `/api/public/questions` endpoint for a list read.
 *
 * This is the SINGLE definition of what a list request looks like on the wire,
 * and the ADR-028 Slice 3 cache derives its list key from this exact string.
 * Two option objects therefore share a cache key if and only if they would
 * produce the same request, which is what makes the key collision-free by
 * construction rather than by a parallel normalisation routine that could
 * drift from the transport.
 *
 * Normalisation is deliberately limited to genuinely inert differences:
 *
 * - `status` is de-duplicated and sorted. The Worker validates it as a SET
 *   (membership against `PUBLIC_VISIBLE_STATUSES`), so ordering carries no
 *   meaning and `['A','B']` / `['B','A']` must not split the cache.
 *
 * Absent parameters are deliberately NOT rewritten to the caller's defaults.
 * A missing `limit` means "whatever the server default is" (20), while
 * `usePublicQuestions` defaults to 10 — so folding "absent" into "10" would
 * serve 10-item data for a 20-item request. Presence is semantic; order is not.
 */
export function buildPublicListEndpoint(options: PublicQuestionListOptions = {}): string {
  const params = new URLSearchParams();
  if (options.page) params.set('page', String(options.page));
  if (options.limit) params.set('limit', String(options.limit));
  if (options.status?.length) {
    params.set('status', [...new Set(options.status)].sort().join(','));
  }

  const queryString = params.toString();
  return queryString ? `/api/public/questions?${queryString}` : '/api/public/questions';
}

/** Build the canonical `/api/public/questions/:id` endpoint for a detail read. */
export function buildPublicDetailEndpoint(questionId: string): string {
  return `/api/public/questions/${encodeURIComponent(questionId)}`;
}

/**
 * Tenant + origin scope for cache keys (ADR-028 Slice 3).
 *
 * Store identity travels as the `X-Store-Hash` HEADER, not in the URL, so an
 * endpoint string alone does not distinguish two stores, and the key carries the
 * scope explicitly. Being precise about what this does and does not buy: the
 * cache lives in module scope, so two widget instances for different stores
 * already get independent caches and this segment is not what separates them.
 * It is defence in depth — it keeps the key correct if the module is ever shared
 * — and it makes the tenant binding of an entry visible in the key itself.
 * `API_BASE` is included because the same path against a different origin is a
 * different resource.
 *
 * The separator is an explicit `\u0000` escape rather than a literal control
 * character: it cannot occur in a URL or a store hash, so the two components can
 * never run together ambiguously, and writing it as an escape keeps this file
 * plain UTF-8 text. A raw NUL byte here would make the whole file read as binary,
 * and recursive text tools — including this repository's own `verify:docs` and
 * `verify:patterns` scans — would silently skip the file that defines the
 * credential boundary.
 *
 * Contains no credential, capability, cookie, email, or correlation material.
 */
export function getPublicReadScope(): string {
  return `${API_BASE}\u0000${STORE_HASH ?? ''}`;
}

/**
 * Widget API client.
 * All methods automatically include the store hash from the widget script URL.
 */
export const widgetApi = {
  /**
   * Check if the widget was initialized successfully.
   * Returns false if store hash could not be extracted from script URL.
   */
  isInitialized(): boolean {
    return STORE_HASH !== null;
  },

  /**
   * Get the current store hash (for debugging/display purposes).
   */
  getStoreHash(): string | null {
    return STORE_HASH;
  },

  questions: {
    /**
     * Submit a new question.
     *
     * @param data - Question data (title, description, posterName, posterEmail, turnstileToken)
     * @returns Created question details including questionId and slug
     */
    async submit(
      data: SubmitQuestionRequest
    ): Promise<ApiResponse<SubmitQuestionResult>> {
      return apiRequest<SubmitQuestionResult>('/api/public/questions', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    /**
     * Get questions submitted by the current guest.
     * Uses the guest_token cookie set by the server.
     *
     * My Questions exposes an explicit customer-controlled retry action. Disable
     * generic transport retries so one mount or retry intent maps to one private
     * history request. Treat HTTP statuses as responses, not transport failures,
     * so the Worker's structured error envelope survives a one-shot 5xx. Actual
     * network failures still fail after that single attempt.
     *
     * @returns List of questions with basic info
     */
    async getMyQuestions(options?: {
      signal?: AbortSignal;
    }): Promise<ApiResponse<MyQuestionsResult>> {
      return apiRequest<MyQuestionsResult>(
        '/api/public/my-questions',
        { signal: options?.signal },
        { maxRetries: 0, retryableStatuses: [] }
      );
    },

    /**
     * Find similar questions based on search text.
     * Used for duplicate detection during question submission (P1-19).
     *
     * @param query - Search query string (typically the question title)
     * @param limit - Maximum results to return (default: 5, max: 10)
     * @param options - Optional request options including AbortSignal
     * @returns Array of similar questions with similarity scores
     */
    async findSimilar(
      query: string,
      limit: number = 5,
      options?: { signal?: AbortSignal }
    ): Promise<ApiResponse<SimilarQuestionsResult>> {
      const params = new URLSearchParams({
        q: query,
        limit: String(Math.min(Math.max(1, limit), 10)),
      });
      return apiRequest<SimilarQuestionsResult>(
        `/api/public/questions/similar?${params.toString()}`,
        { signal: options?.signal }
      );
    },

    /**
     * Get paginated list of approved public questions (GAP-005).
     *
     * ADR-028 Slice 3: this is a public, moderation-gated read, so it uses the
     * anonymous `credentials: 'omit'` transport and is cacheable. Nothing about
     * the response depends on the visitor's cookie or capability handles.
     *
     * @param options - Pagination options
     * @returns Paginated questions list
     */
    async getPublic(
      options: {
        page?: number;
        limit?: number;
        status?: readonly PublicVisibleStatus[];
        signal?: AbortSignal;
      } = {}
    ): Promise<ApiResponse<QuestionListResponse>> {
      return anonymousGetRequest<QuestionListResponse>(buildPublicListEndpoint(options), {
        signal: options.signal,
      });
    },

    /**
     * Search public-visible questions through the existing Lane B-2 endpoint.
     *
     * Typeahead owns its bounded retry/backoff policy, so this method disables
     * generic transport retries. One user intent therefore maps to one request;
     * a 429 can never be amplified by the shared 5xx/network retry helper.
     */
    async search(
      query: string,
      options: {
        page?: number;
        limit?: number;
        status?: readonly PublicVisibleStatus[];
        signal?: AbortSignal;
      } = {}
    ): Promise<ApiResponse<PublicQuestionSearchResult>> {
      const params = new URLSearchParams({ q: query.trim() });
      if (options.page) params.set('page', String(options.page));
      if (options.limit) params.set('limit', String(options.limit));
      if (options.status?.length) params.set('status', options.status.join(','));

      return apiRequest<PublicQuestionSearchResult>(
        `/api/public/questions/search?${params.toString()}`,
        { signal: options.signal },
        { maxRetries: 0 }
      );
    },

    /**
     * Get a single question with its approved replies (GAP-005).
     *
     * ADR-028 Slice 3: public read, so it uses the anonymous
     * `credentials: 'omit'` transport and is cacheable.
     *
     * ANALYTICS CONSEQUENCE (recorded, not compensated for): the Worker
     * increments a bucketed public view count on this GET and de-duplicates
     * repeat views by guest cookie when one is present, falling back to client
     * IP otherwise. Omitting credentials means this request now always
     * de-duplicates by IP, and cache hits suppress repeat requests entirely.
     * The resulting analytics bias is mixed: shared/NAT IPs and cache hits can
     * undercount, while IP churn or fail-open/unidentifiable requests can
     * overcount. ADR-028 mandates anonymous detail caching; Worker behaviour is
     * deliberately NOT changed to compensate within this slice.
     *
     * @param questionId - The question ID
     * @param options - Optional request options including AbortSignal
     * @returns Question detail with replies
     */
    async getDetail(
      questionId: string,
      options: { signal?: AbortSignal } = {}
    ): Promise<ApiResponse<QuestionDetailResponse>> {
      return anonymousGetRequest<QuestionDetailResponse>(buildPublicDetailEndpoint(questionId), {
        signal: options.signal,
      });
    },
  },

  /**
   * Image upload operations.
   *
   * DEVIATION FROM SPEC: Spec §2.5.1 shows `questions.uploadImage()`, but we use
   * `images.upload()` for better architectural separation:
   *
   * 1. RESTful alignment: Images are a sub-resource, matches API path `/questions/:id/images`
   * 2. Separation of concerns: Media operations distinct from question CRUD
   * 3. Scalability: Future reply images fit naturally as `images.uploadForReply()`
   * 4. Domain modeling: Clear boundary between content and media domains
   *
   * See PHASE_0B_SPEC.md Decision Log for rationale.
   */
  images: {
    /**
     * Upload an image for a question.
     *
     * @param questionId - The question to attach the image to
     * @param file - The image file to upload
     * @returns Upload result with attachment details
     */
    async upload(
      questionId: string,
      file: File,
      uploadProof?: string
    ): Promise<ApiResponse<UploadImageResult>> {
      return uploadImageMultipart(
        `/api/public/questions/${encodeURIComponent(questionId)}/images`,
        file,
        uploadProof
      );
    },

    /**
     * Upload an image for a reply (ADR-022 §12 PR5-remainder).
     *
     * Mirrors {@link upload} exactly but targets the reply-anchored endpoint
     * `POST /api/public/replies/:replyId/images` (guarded server-side by
     * `uploadCapabilityMiddleware('reply')`). The `uploadProof` is the
     * reply-scoped ADR-024 §11 upload capability carried on the reply-create
     * response, re-sent here as `X-Upload-Proof` (never a cookie/URL) so the
     * upload survives third-party-cookie blocking. Reuses `apiUpload`, so it
     * inherits the `maxRetries: 0` invariant (each attempt consumes one
     * capability use server-side, so transport auto-retry is unsafe).
     *
     * @param replyId - The reply to attach the image to
     * @param file - The image file to upload
     * @param uploadProof - Opaque reply-scoped upload-proof handle (memory-only)
     * @returns Upload result with attachment details
     */
    async uploadForReply(
      replyId: string,
      file: File,
      uploadProof?: string
    ): Promise<ApiResponse<UploadImageResult>> {
      return uploadImageMultipart(
        `/api/public/replies/${encodeURIComponent(replyId)}/images`,
        file,
        uploadProof
      );
    },
  },

  replies: {
    /**
     * Submit a reply to a question.
     *
     * @param questionId - The question to reply to
     * @param data - Reply data (body, authorName, authorEmail, turnstileToken)
     * @returns Submission result with replyId and status
     */
    async submit(
      questionId: string,
      data: {
        body: string;
        authorName: string;
        authorEmail: string;
        turnstileToken: string;
        customerId?: string;
        consentToPublicDisplay: boolean; // BL-PRIVACY-CONSENT-CAPTURE-001
      }
    ): Promise<ApiResponse<SubmitReplyResponse>> {
      return apiRequest<SubmitReplyResponse>(
        `/api/public/questions/${encodeURIComponent(questionId)}/replies`,
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      );
    },
  },

  config: {
    /**
     * Get public widget configuration.
     * This endpoint does NOT require store hash header.
     *
     * @param options - Optional request options including AbortSignal
     * @returns Widget configuration (Turnstile key, limits)
     */
    async get(options?: { signal?: AbortSignal }): Promise<ApiResponse<WidgetConfigResult>> {
      // Config endpoint doesn't need X-Store-Hash but we include it anyway
      // for consistency (server ignores it)
      return apiRequest<WidgetConfigResult>('/api/public/config', { signal: options?.signal });
    },
  },

  availability: {
    /**
     * Get tenant public availability (Phase 4.5 controlled-launch slice).
     *
     * Tenant-aware: requires X-Store-Hash (added automatically by apiRequest).
     * The widget uses this to decide whether to render the normal surface
     * (VISIBLE) or a suppressed unavailable/coming-soon panel. Callers MUST
     * treat any non-success response as "not available" (fail closed).
     *
     * @param options - Optional request options including AbortSignal
     * @returns Resolved availability (launch mode + derived flags/copy).
     */
    async get(options?: { signal?: AbortSignal }): Promise<ApiResponse<PublicAvailability>> {
      return apiRequest<PublicAvailability>('/api/public/availability', {
        signal: options?.signal,
      });
    },
  },

  tags: {
    /**
     * Get tag suggestions for question content.
     *
     * @param query - Text to analyze for tag suggestions
     * @param limit - Maximum tags to return (default: 5, max: 10)
     * @returns Suggested tags and top tags
     */
    async suggest(
      query: string,
      limit: number = 5
    ): Promise<ApiResponse<TagSuggestionResponse>> {
      const trimmed = query.trim();
      if (!trimmed) {
        return { success: true, data: { suggestions: [], topTags: [] } };
      }
      const params = new URLSearchParams({ q: trimmed });
      if (limit !== 5) params.set('limit', String(limit));
      return apiRequest<TagSuggestionResponse>(
        `/api/public/tags/suggest?${params.toString()}`
      );
    },

    /**
     * Get the tag taxonomy for UI display.
     *
     * @returns Filtered taxonomy and allowed tags
     */
    async taxonomy(): Promise<ApiResponse<PublicTagTaxonomyResponse>> {
      return apiRequest<PublicTagTaxonomyResponse>('/api/public/tags/taxonomy');
    },
  },

  reputation: {
    /**
     * Get the current user's reputation points and badges.
     *
     * @param customerId - Optional customer ID for logged-in users
     * @returns User reputation data with display name, points, badges, and badge details
     */
    async get(
      customerId?: string
    ): Promise<ApiResponse<PublicReputationResponse>> {
      const params = new URLSearchParams();
      if (customerId) params.set('customerId', customerId);
      const queryString = params.toString();
      const endpoint = `/api/public/reputation${queryString ? `?${queryString}` : ''}`;
      return apiRequest<PublicReputationResponse>(endpoint);
    },

    /**
     * Get top users by reputation points.
     *
     * @param limit - Number of top users to return (default: 10, max: 50)
     * @returns Public leaderboard with ranks
     */
    async leaderboard(
      limit?: number
    ): Promise<ApiResponse<LeaderboardResponse>> {
      const params = new URLSearchParams();
      if (limit !== undefined) params.set('limit', String(limit));
      const queryString = params.toString();
      const endpoint = `/api/public/reputation/leaderboard${queryString ? `?${queryString}` : ''}`;
      return apiRequest<LeaderboardResponse>(endpoint);
    },
  },

  products: {
    /**
     * Track a click on a suggested product link.
     *
     * @param questionId - The question where the product was displayed
     * @param productId - The BigCommerce product ID that was clicked
     * @returns Tracking confirmation
     */
    async trackClick(
      questionId: string,
      productId: number
    ): Promise<ApiResponse<{ tracked: boolean }>> {
      return apiRequest<{ tracked: boolean }>('/api/public/products/click', {
        method: 'POST',
        body: JSON.stringify({ questionId, productId }),
      });
    },
  },
};

// Export types for consumers
export type { SubmitQuestionRequest, SubmitReplyResponse };
